import CirclePattern from "./CirclePattern";

const BENEFITS = [
  {
    title: "Simplified system design",
    text: "ecoHeatwave removes the need for complex plant equipment, pipework, and wet heating circuits — reducing system complexity from the outset.",
  },
  {
    title: "Faster installation",
    text: "With pre-engineered components and no wet system infrastructure, ecoHeatwave can be installed in a fraction of the time of an air source heating system, helping to accelerate build programmes.",
  },
  {
    title: "Lower capital cost (in integrated systems)",
    text: "When combined with solar PV, battery storage, and air source hot water cylinders, ecoHeatwave can form part of a fully integrated system that is often more cost-effective than comparable whole-house air source heating solutions.",
  },
  {
    title: "No annual servicing requirement",
    text: "With no moving parts and no mechanical plant, ecoHeatwave systems do not require annual servicing — reducing ongoing maintenance costs and operational burden.",
  },
  {
    title: "Long-term reliability",
    text: "ecoHeatwave panels are solid-state with no mechanical wear components and are supported by a 20-year manufacturer's warranty, providing long-term confidence for developers and homeowners.",
  },
  {
    title: "Greater design flexibility",
    text: "Covering approximately 30% of the ceiling area, ecoHeatwave preserves valuable space for lighting, ventilation, and other services — offering greater freedom in architectural and services design.",
  },
  {
    title: "Reduced space requirements",
    text: "No external units, plant rooms, or internal bulky equipment are required, freeing up valuable internal and external space within the building.",
  },
  {
    title: "Zoned, responsive control",
    text: "Each room or zone is independently controlled, allowing precise heating based on occupancy and usage — improving comfort while reducing unnecessary energy use.",
  },
  {
    title: "Quiet, invisible operation",
    text: "With no fans, compressors, or moving components, ecoHeatwave operates silently and invisibly — enhancing occupant comfort without impacting the living environment.",
  },
  {
    title: "Seamless integration with modern energy systems",
    text: "ecoHeatwave integrates easily with solar PV, battery storage, and smart energy controls — supporting efficient, demand-led energy use across the building.",
  },
];

const COMPARISON_ROWS = [
  { feature: "Installation time", eco: "Fast", ashp: "Longer" },
  { feature: "System complexity", eco: "Low", ashp: "Higher" },
  { feature: "Maintenance", eco: "Minimal", ashp: "Annual servicing" },
  { feature: "Space requirement", eco: "Minimal", ashp: "External + internal plant" },
  { feature: "Design flexibility", eco: "High", ashp: "More constrained" },
  { feature: "Capital cost", eco: "Lower", ashp: "Higher" },
  { feature: "Warranty", eco: "20 years", ashp: "2–5 years" },
];

export default function WhyEcoHeatwave() {
  return (
    <section id="why" data-testid="why-section" className="overflow-hidden">
      <WhyHeader />
      <BenefitsGrid />
      <ComparisonTable />
      <ClosingDarkBanner />
      <ClosingTagline />
    </section>
  );
}

function WhyHeader() {
  return (
    <div className="py-10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-4xl">
          <div className="overline mb-6">Why ecoHeatwave</div>
          <h2
            data-testid="why-headline"
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-[-0.02em] leading-[1.05] text-ecoh-ink"
          >
            Why choose ecoHeatwave.
          </h2>
          <p className="mt-6 max-w-2xl text-ecoh-ink/70 text-lg md:text-xl font-light leading-snug">
            A simpler, more flexible approach to modern heating.
          </p>
          <p className="mt-10 max-w-3xl text-ecoh-muted text-base md:text-lg font-light leading-relaxed">
            ecoHeatwave offers a fundamentally different approach to heating
            — designed to reduce system complexity, improve installation
            efficiency, and support modern low-carbon building strategies.
          </p>
        </div>
      </div>
    </div>
  );
}

function BenefitCard({ benefit, index }) {
  return (
    <div data-testid={`why-benefit-${index}`} className="flex flex-col">
      <div className="flex items-baseline gap-5">
        <span className="text-xs tracking-[0.22em] uppercase text-ecoh-accent shrink-0">
          {String(index + 1).padStart(2, "0")}
        </span>
        <h4 className="font-display text-xl md:text-2xl font-light tracking-tight leading-snug text-ecoh-ink">
          {benefit.title}
        </h4>
      </div>
      <p className="mt-4 ml-[3.25rem] text-ecoh-ink/75 text-base md:text-[17px] font-light leading-relaxed">
        {benefit.text}
      </p>
    </div>
  );
}

function BenefitsGrid() {
  return (
    <div className="bg-ecoh-surface">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="max-w-3xl">
          <h3 className="font-display text-2xl md:text-3xl lg:text-4xl font-light tracking-[-0.02em] leading-[1.1] text-ecoh-ink">
            Key benefits compared to air source systems.
          </h3>
        </div>
        <div className="mt-14 md:mt-16 grid grid-cols-1 md:grid-cols-2 gap-x-12 lg:gap-x-20 gap-y-12">
          {BENEFITS.map((b, i) => (
            <BenefitCard key={b.title} benefit={b} index={i} />
          ))}
        </div>
      </div>
    </div>
  );
}

function ComparisonRow({ row, index }) {
  return (
    <div
      data-testid={`compare-row-${index}`}
      className={`grid grid-cols-3 border-t border-ecoh-ink/10 ${
        index % 2 === 0 ? "bg-ecoh-bg" : "bg-ecoh-surface"
      }`}
    >
      <div className="px-6 md:px-8 py-5 md:py-6 text-ecoh-ink/70 text-sm md:text-base font-light">
        {row.feature}
      </div>
      <div className="px-6 md:px-8 py-5 md:py-6 border-l border-ecoh-ink/10 flex items-center gap-3">
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-ecoh-accent shrink-0" />
        <span className="font-display text-base md:text-lg font-normal tracking-tight text-ecoh-ink">
          {row.eco}
        </span>
      </div>
      <div className="px-6 md:px-8 py-5 md:py-6 border-l border-ecoh-ink/10 text-ecoh-ink/65 text-sm md:text-base font-light">
        {row.ashp}
      </div>
    </div>
  );
}

function ComparisonTable() {
  return (
    <div className="py-10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          <h3 className="font-display text-2xl md:text-3xl lg:text-4xl font-light tracking-[-0.02em] leading-[1.1] text-ecoh-ink">
            ecoHeatwave vs ASHP.
          </h3>
        </div>
        <div
          data-testid="why-comparison-table"
          className="mt-14 overflow-hidden rounded-2xl ring-1 ring-ecoh-ink/10"
        >
          <div className="grid grid-cols-3 bg-ecoh-ink text-white">
            <div className="px-6 md:px-8 py-5 text-[11px] tracking-[0.22em] uppercase text-white/60">
              Feature
            </div>
            <div className="px-6 md:px-8 py-5 font-display text-base md:text-lg font-normal tracking-tight text-white border-l border-white/10">
              ecoHeatwave
            </div>
            <div className="px-6 md:px-8 py-5 font-display text-base md:text-lg font-light tracking-tight text-white/85 border-l border-white/10">
              ASHP
            </div>
          </div>
          {COMPARISON_ROWS.map((row, i) => (
            <ComparisonRow key={row.feature} row={row} index={i} />
          ))}
        </div>
      </div>
    </div>
  );
}

function ClosingDarkBanner() {
  return (
    <div className="relative bg-ecoh-ink text-white overflow-hidden">
      <CirclePattern
        className="pointer-events-none select-none absolute inset-0 w-full h-full"
        opacity={0.5}
      />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-ecoh-ink via-ecoh-ink/80 to-ecoh-ink/40" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <p
          data-testid="why-closing"
          className="max-w-4xl font-display text-2xl md:text-3xl lg:text-4xl font-light tracking-[-0.02em] leading-[1.15] text-white"
        >
          Rather than adding complexity to achieve performance, ecoHeatwave
          delivers efficient, low-carbon heating through{" "}
          <span className="text-ecoh-amber">simplicity, integration, and intelligent design.</span>
        </p>
      </div>
    </div>
  );
}

function ClosingTagline() {
  return (
    <div className="py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <p
          data-testid="why-tagline"
          className="font-display font-light tracking-tight text-ecoh-ink/85 leading-snug whitespace-nowrap text-[clamp(1.25rem,3.2vw,2.5rem)]"
        >
          Designed for where buildings are going—not where they&apos;ve been.
        </p>
      </div>
    </div>
  );
}
